import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { deleteApiKey, getListApiKeysQueryKey } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';

// The API deletes one key per request, so a batch is a fan-out: `allSettled` lets every request
// finish, and a partial failure still removed keys — hence refetching in `onSettled`, not `onSuccess`.
export const useRevokeApiKeys = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (apiKeyIds: string[]) => {
            const results = await Promise.allSettled(apiKeyIds.map(apiKeyId => deleteApiKey({ apiKeyId })));
            const failedCount = results.filter(result => result.status === 'rejected').length;

            if (failedCount > 0) {
                throw new Error(`Failed to revoke ${failedCount} of ${apiKeyIds.length} API keys`);
            }

            return apiKeyIds.length;
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: getListApiKeysQueryKey() });
        },
        onSuccess: count =>
            dispatch(
                messagesActions.displaySuccessMessage({
                    message: { id: 'api-keys.batchRevoke.success', values: { count } },
                    options: { duration: 5 },
                }),
            ),
        onError: () =>
            dispatch(
                messagesActions.displayErrorMessage({
                    message: { id: 'api-keys.batchRevoke.error' },
                    options: { duration: 5 },
                }),
            ),
    });
};
